import * as React from "react";

type LazyOpts = {
  /** Min-height des Skeleton-Wrappers, verhindert Layout-Shift. */
  minHeight?: number | string;
  /** Zusätzliche Klassen am Fallback-Wrapper. */
  fallbackClassName?: string;
};

/**
 * Lädt eine Sektion clientseitig nach (Below-the-Fold-Blocks).
 *
 * - SSR rendert nur den Fallback (stabile Höhe → kein Layout-Shift).
 * - Client startet den Import erst nach Hydration → weniger initiales JS.
 * - Daten aus dem TanStack-Query-Cache (via Loader/`ensureQueryData`)
 *   stehen unverändert bereit, wenn der Chunk landet.
 *
 * Verwendung:
 * ```ts
 * const Foo = lazySection(async () => {
 *   const m = await import("@/components/Foo");
 *   return { default: m.Foo };
 * }, { minHeight: 400 });
 * ```
 */
export function lazySection<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  opts: LazyOpts = {},
): T {
  const LazyInner = React.lazy(factory);
  const { minHeight, fallbackClassName } = opts;

  const Section = ((props: React.ComponentProps<T>) => {
    const [mounted, setMounted] = React.useState(false);
    React.useEffect(() => setMounted(true), []);

    const fallback = (
      <div
        aria-hidden="true"
        aria-busy="true"
        className={fallbackClassName}
        style={minHeight !== undefined ? { minHeight } : undefined}
      />
    );

    if (!mounted) return fallback;

    return (
      <React.Suspense fallback={fallback}>
        <LazyInner {...(props as React.ComponentProps<typeof LazyInner>)} />
      </React.Suspense>
    );
  }) as unknown as T;

  (Section as unknown as { displayName?: string }).displayName = "LazySection";
  return Section;
}
